/* eslint-disable no-nested-ternary */
import { createSlice } from '@reduxjs/toolkit';
import moment from 'moment';
import dayjs from 'dayjs';
import { dueBucketParams } from 'src/sections/tasks/effectiveDue';
import { practiceInSuffix } from 'src/utils/practiceInSuffix';
import { callApi } from '../../utils/apiCall';
import { buildApiErrorPayload } from '../../utils/apiErrorPayload';
import { hasFeatureAccess } from '../../utils/featurePermission';
import { isOldRemindersDisabled } from '../../utils/oldRemindersDisabled';
import { counterOnlyParam } from '../../constants/fields';
import { LEAD_PROVIDER_PARAM, TREATMENT_LEAD_PROVIDER_PARAM } from '../../utils/healthcareProviderScope';

import { updateError } from './user';
import { loadCustomTabs } from './customTableTabs';

import { dispatch, store } from '../store';
import { getPracticeId } from './clincheck';

// Tasks-v2 follow-up badges — count the same set the V2 task board surfaces as
// actionable "now": Overdue + Due Today, whole practice (no assignee filter),
// status in pending/in_progress. We read only the DRF `count` from each (limit=1)
// and keep them BOTH so the dashboard card shows "Overdue" and "Due Today" as two
// separate, labelled numbers. `total` is still exposed so any single-number
// consumer keeps working unchanged.
//
// Date-boundary semantics (Raj-approved, aligned with TaskColumnView/TaskBoardView
// and the V1 To-do lane precedent in taskThunks):
//   Overdue  = due_at BEFORE the start of today (calendar-date, not moment-in-time).
//              A task due at 09:00 today is NOT overdue — it is Due Today.
//   Due Today = due_at within today (>= todayStart, <= todayEnd) regardless of whether
//              that time has already passed.
//
// Previously used is_overdue=true / is_overdue=false. The BE `is_overdue` flag is
// moment-in-time: it flips true the instant due_at passes, so a 09:00 task becomes
// "overdue" by 09:01 and is absorbed into the Overdue badge, leaving Due Today=0.
// Date-boundary params (due_at__lt / due_at__gte+lte) avoid this.
//
// `stage` scopes to V2 tasks hanging off lead_treatments of that status — the SAME
// param the V2 board's lead-status filter uses. Splits the dashboard follow-up widgets:
//   '2' New patient follow ups   → stage='NEW'
//   '4' Consultation follow ups  → stage='CONSULTATION_ATTENDED'
export async function getV2FollowUpTaskCount(practiceId, stage) {
    const status = 'status__in=pending,in_progress';
    const stageParam = stage ? `&lead_treatment__status__in=${encodeURIComponent(stage)}` : '';
    // Pause-aware buckets (BE due_bucket): a task whose open checklist items are
    // all paused into the future is not overdue / due today — the same rule as
    // the Task Board the badge click lands on. The legacy raw window is sent
    // too, for a backend without due_bucket (see dueBucketParams).
    const qs = (params) => new URLSearchParams(params).toString();
    const overdueQuery = `?${qs(dueBucketParams('overdue', dayjs))}&${status}${stageParam}&limit=1&fields={id}`;
    const todayQuery = `?${qs(dueBucketParams('today', dayjs))}&${status}${stageParam}&limit=1&fields={id}`;

    const [overdueRes, todayRes] = await Promise.all([
        callApi(`/api/v1/practices/${practiceId}/tasks/${overdueQuery}`),
        callApi(`/api/v1/practices/${practiceId}/tasks/${todayQuery}`),
    ]);

    const readCount = (res) => (res?.status === 200 && typeof res?.data?.count === 'number' ? res.data.count : 0);
    const overdue = readCount(overdueRes);
    const dueToday = readCount(todayRes);
    return { overdue, dueToday, total: overdue + dueToday };
}

// WhatsApp widget badge for tasks-v2 practices (key '7').
// Counts tasks with checklist_item_type__in=WHATSAPP_SENT + status pending/in_progress,
// matching the exact filter the V2 board applies when the widget click lands on
// ?item_type__in=WHATSAPP_SENT (FE key → BE checklist_item_type__in, per
// TaskBoardView.buildParams). The board groups tasks by lead client-side, but
// the badge uses raw task count — consistent with the follow-up widgets ('2','4')
// which also badge by task count. A single count-only call is sufficient.
async function getV2WhatsAppTaskCount(practiceId) {
    const res = await callApi(
        `/api/v1/practices/${practiceId}/tasks/?checklist_item_type__in=WHATSAPP_SENT&status__in=pending,in_progress&limit=1&fields={id}`
    );
    return res?.status === 200 && typeof res?.data?.count === 'number' ? res.data.count : 0;
}

// All Practices phase-2 consumer: if the user has a multi-practice
// selection, every widget count aggregates across those practices so
// the dashboard badges match the lead-table rows below them.
// Returns '' for single-practice — the URL's practiceId already scopes.
const _activePracticesSuffix = () =>
    practiceInSuffix(store.getState().practiceTreatmentList?.activePracticeIds);


const initialState = {
    // Archive-audit summary (Phase 2, 2026-07-05) — whole-filtered-dataset
    // aggregation from /archived/summary/; null until first load.
    archivedSummary: null,
    archivedSummaryLoading: false,
    isLoading: false,
    widgetCounter: [],
    isLoadingWidgetCounter: true,
    // Track which widget keys have finished loading (for skeleton → loaded transition)
    loadedKeys: [],
    // Total number of widgets expected (set when reload starts)
    expectedCount: 0,
    // Tab-contextual attended widget counts (tasks-v2 + custom tabs path).
    // Null = not yet fetched / cleared between tab switches.
    attendedTabCounts: null,
    isLoadingAttendedTabCounts: false,
    // Launchpad "due now" per CUSTOM TAB: { attended: { [tabId]: n|null } | null, started: {...} | null }.
    // null = not fetched yet; {} = the table has no custom tabs. See getLaunchpadDueNowByTab.
    dueNowByTab: { attended: null, started: null },
    // New Lead table "Tasks / Due" mode widget counts (tasks-v2 practices only).
    // Null = not yet fetched / cleared on practice switch or mode exit.
    newLeadTaskCounts: null,
    isLoadingNewLeadTaskCounts: false,
    // Treatment Started checklist strip counts — populated by getTsChecklistCounts on
    // every custom-tab switch. Null = not yet fetched / cleared between tab switches.
    // Keyed separately from attendedTabCounts so the two tables can't cross-pollute.
    tsChecklistCounts: null,
    // Treatment Started contextual widget counts: Due Now (overdue + due_today),
    // No Next Action, and Completing This Month. Fetched once on mount and whenever
    // the practice or active tab changes. Null = not yet fetched / cleared on practice switch.
    tsContextualCounts: null,
    isLoadingTsContextualCounts: false,
};

const slice = createSlice({
    name: 'widgetCounter',
    initialState,
    reducers: {
        setArchivedSummary(state, action) {
            state.archivedSummary = action.payload;
            state.archivedSummaryLoading = false;
        },
        setArchivedSummaryLoading(state, action) {
            state.archivedSummaryLoading = action.payload;
        },
        reset(state) {
            Object.assign(state, initialState);
        },
        setWidgetCounter(state, action) {
            state.widgetCounter = action.payload;
        },
        upsertWidgetCounter(state, action) {
            const { key, ...rest } = action.payload;
            const idx = state.widgetCounter.findIndex(item => item.key === key);
            if (idx !== -1) {
                state.widgetCounter[idx] = { ...state.widgetCounter[idx], ...rest };
            } else {
                state.widgetCounter.push({ key, ...rest });
            }
            // Mark this key as loaded
            if (!state.loadedKeys.includes(key)) {
                state.loadedKeys.push(key);
            }
        },
        // Called when a new widget reload cycle starts
        startWidgetLoading(state, action) {
            // Only reset loadedKeys on first load (when no data exists yet).
            // On subsequent reloads (user actions), keep existing data visible
            // while new data loads in the background.
            if (state.widgetCounter.length === 0) {
                state.loadedKeys = [];
            }
            state.expectedCount = action.payload || 0;
        },
        // Tab-contextual attended widget counts (tasks-v2 + custom tabs)
        setAttendedTabCountsLoading(state, action) {
            state.isLoadingAttendedTabCounts = action.payload;
        },
        setAttendedTabCounts(state, action) {
            state.attendedTabCounts = action.payload;
            state.isLoadingAttendedTabCounts = false;
        },
        setDueNowByTab(state, action) {
            const { kind, counts } = action.payload;
            state.dueNowByTab = { ...state.dueNowByTab, [kind]: counts };
        },
        clearDueNowByTab(state) {
            state.dueNowByTab = { attended: null, started: null };
        },
        clearAttendedTabCounts(state) {
            state.attendedTabCounts = null;
            state.isLoadingAttendedTabCounts = false;
        },
        // New Lead "Tasks / Due" mode widget counts
        setNewLeadTaskCountsLoading(state, action) {
            state.isLoadingNewLeadTaskCounts = action.payload;
        },
        setNewLeadTaskCounts(state, action) {
            state.newLeadTaskCounts = action.payload;
            state.isLoadingNewLeadTaskCounts = false;
        },
        clearNewLeadTaskCounts(state) {
            state.newLeadTaskCounts = null;
            state.isLoadingNewLeadTaskCounts = false;
        },
        // Treatment Started checklist strip counts
        setTsChecklistCounts(state, action) {
            state.tsChecklistCounts = action.payload;
        },
        clearTsChecklistCounts(state) {
            state.tsChecklistCounts = null;
        },
        // Treatment Started contextual widget counts (Due Now / In Progress / Follow-Up
        // Complete / go-aheads / Started This Month / value). Replaces the whole object so
        // the count cards paint as soon as the (fast) count queries resolve.
        setTsContextualCounts(state, action) {
            state.tsContextualCounts = action.payload;
            state.isLoadingTsContextualCounts = false;
        },
        // Merge-in for the DEFERRED go-aheads breakdown + the date-scoped (Started / Total
        // Value) refetch — both land after the headline counts so the cards paint first and
        // these fill in. Staleness is guarded by the caller's generation-ref before dispatch.
        //
        // Initialises from {} when nothing has painted yet: the old `if (state.
        // tsContextualCounts)` guard silently DROPPED the payload on a cold load (counts still
        // null) and left isLoadingTsContextualCounts stuck true — the date-scoped Started/Value
        // numbers were fetched, returned 200, and then thrown away.
        mergeTsContextualCounts(state, action) {
            state.tsContextualCounts = { ...(state.tsContextualCounts || {}), ...action.payload };
            state.isLoadingTsContextualCounts = false;
        },
        setTsContextualCountsLoading(state, action) {
            state.isLoadingTsContextualCounts = action.payload;
        },
        clearTsContextualCounts(state) {
            state.tsContextualCounts = null;
            state.isLoadingTsContextualCounts = false;
        },
    }
});

export default slice.reducer;

export const {
    setWidgetCounter,
    upsertWidgetCounter,
    reset,
    startWidgetLoading,
    setAttendedTabCounts,
    setAttendedTabCountsLoading,
    setDueNowByTab,
    clearDueNowByTab,
    clearAttendedTabCounts,
    setNewLeadTaskCounts,
    setNewLeadTaskCountsLoading,
    clearNewLeadTaskCounts,
    setTsChecklistCounts,
    clearTsChecklistCounts,
    setTsContextualCounts,
    mergeTsContextualCounts,
    setTsContextualCountsLoading,
    clearTsContextualCounts, setArchivedSummary, setArchivedSummaryLoading } = slice.actions;

export const getDateRange = (date) => {
    const filteredDate = date.split(',');
    const customStartDate = `${filteredDate[0]} 00:00:00`;
    const customEndDate = `${filteredDate[1]} 23:59:00`;
    return `${customStartDate},${customEndDate}`;
};

export const getQueryTask = (data) => {
    const { isTasksCompleted, assigneeUser, unassignedUser, filter } = data.taskFilter
    let query = `?lead__is_archived=false&ordering=datetime&is_completed=${isTasksCompleted}`;

    if (filter === 'TODO') {
        query += `&group__in=TODO`
    } else if (filter === "CALLBACK") {
        query += `&group__in=CALLBACK_REQUEST`
    } else {
        query += `&group__in=TODO,FOLLOW_UP,CALLBACK_REQUEST`
    }
    if (assigneeUser) {
        query += `&assignees__user__id__in=${assigneeUser}`
    }
    if (unassignedUser) {
        query += '&assignees__isnull=true'
    }
    if (filter != null && filter !== 'TODO' && filter !== 'leadNotes' && filter !== '' && filter !== "CALLBACK" && filter !== 'UNANSWERED' && filter !== 'undefined') {
        query += `&lead_treatment__status=${filter}`
    }
    if (data.dateFilters === 'Upcoming') {
        query += `&datetime__date__gte=${moment(new Date().setDate(new Date().getDate() + 1)).format('YYYY-MM-DD')}`
    } else if (data.dateFilters === 'Today') {
        query += `&datetime__date__lte=${moment(new Date().setDate(new Date().getDate())).format('YYYY-MM-DD')}`
    }

    if (data.treatment) {
        query += `&lead_treatment__treatment__in=${data.treatment}`
    }

    return query;

}

export const getQuery = (data) => {
    let query = '?is_archived=false';

    if (data?.dateRange) {
        query += `&created_at__range=${getDateRange(data?.dateRange)}`;
    }
    if (data.treatmentStatusForPrice === 'TREATMENT_STARTED' || data.status === 'TREATMENT_STARTED' || data.attendedFlatMode) {
        if (data.startDate && data.endDate) {
            const state = data.attendedFlatMode ? 'CONSULTATION_ATTENDED' : 'TREATMENT_STARTED';
            query += `&state_transition_params={"state":"${state}","timestamp_gte":"${data.startDate} 00:00:00","timestamp_lte":"${data.endDate} 23:59:00"}`;
        }
        // else if (!data.appointmentDateRange) {
        //     query += '&state_transition_params={"state":"TREATMENT_STARTED"}';
        // }
    }
    if (data?.status) {
        if (data?.status === "NEW" && data?.healthCareProvider && data?.hasNewOrNullTreatment) {
            query += `&has_new_or_null_treatment=true`
        } else
            query += `&lead_treatments__status=${data?.status}`;
    }
    if (data?.practiceArray?.length) {
        query += `&practice__in=${data?.practiceArray}`;
    }
    if (data?.treatmentStatus && data.treatmentStatus !== 'undefined') {
        query += `&lead_treatment__status=${data.treatmentStatus}`;
    }
    if (data?.treatment) {
        query += `&lead_treatments__treatment__in=${data?.treatment}`;
    }
    if (data?.reminderContactedCount) {
        query += '&contacted_count=0';
    }
    if (data?.contactedCount && !Number.isNaN(Number(data.contactedCount))) {
        query += `&lead_treatments__lead_contacted_counts__count__gte=${Number(data.contactedCount)}`;
    }
    if (data?.contactedCountForStatus) {
        query += `&contacted_count_for_status=${data.contactedCountForStatus}`;
    }
    if (data?.clinCheckStatus) {
        query += `&lead_treatments__leadtreatment_clincheck__status__in=${data?.clinCheckStatus}`;
    }
    if (data?.isLeadNotes) {
        query += '&latest_note__in=AWAITING_RESPONSE';
    }
    if (data.appointmentDateRange) {
        query += `&lead_treatments__appointments__datetime__range=${getDateRange(data.appointmentDateRange)}`;
    }
    // Upcoming this week - appointments in the next 7 days
    if (data.upcomingThisWeek) {
        const today = moment().format('YYYY-MM-DD');
        const nextWeek = moment().add(7, 'days').format('YYYY-MM-DD');
        query += `&lead_treatments__appointments__datetime__range=${today} 00:00:00,${nextWeek} 23:59:00`;
    }
    // Past appointments with no outcome recorded (BOOKED status but datetime in the past)
    if (data.pastNoOutcome) {
        const yesterday = moment().subtract(1, 'day').format('YYYY-MM-DD');
        query += `&lead_treatments__appointments__datetime__lte=${yesterday} 23:59:00`;
    }
    if (data.treatmentPrice) {
        query += '&aggregate[Sum]=lead_treatments__custom_price';
    }
    if (data.isContactedCountNull) {
        query += '&lead_treatments__lead_contacted_counts__isnull=true';
    }
    if (data.isClinCheckTable) {
        query += '&lead_treatments__leadtreatment_clincheck__isnull=false';
    }
    if (data.group__in && !data.pendingWhatsapp) {
        query += '&group__in=FOLLOW_UP,TODO,NEW_LEAD';
        query += '&lead__is_archived=false';
    }
    if (data.completedFilter && data.is_completed !== undefined && data.is_completed !== null) {
        query += `&is_completed=${data.is_completed}`;
    }
    if (data.isTaskDateTimeRange && data.taskDateRange) {
        query += `&datetime__date__range=${data?.taskDateRange}`;
    } else if (data.dateFilters === 'Today') {
        query += `&datetime__date__lte=${moment(new Date()).format('YYYY-MM-DD')}`;
    } else if (data.dateFilters === 'Upcoming') {
        query += `&datetime__date__gte=${moment(new Date()).add(1, 'day').format('YYYY-MM-DD')}`;
    }
    if (data.treatmentStatusForPrice) {
        query += `&lead_treatments__status__in=${data?.treatmentStatusForPrice}`;
    }
    if (data.appStatus) {
        query += `&lead_treatments__appointments__status=${data?.appStatus}`;
    }
    if (data.appStatusForPrice) {
        query += `&lead_treatments__appointments__status__in=${data?.appStatusForPrice}`;
    }
    if (data?.healthCareProvider) {
        query += `&healthcare_provider=${data?.healthCareProvider}`;
    }
    if (data?.hasNewOrNullTreatment) {
        query += `&has_new_or_null_treatment=${data?.hasNewOrNullTreatment}`;
    }

    return query;
};

export const setCounter = (data, counter, teethCount) => {
    dispatch(upsertWidgetCounter({ key: data.key, count: counter, teethCount }));
};


export function getWidgetCounter(data) {
    return async () => {
        // Static endpoint context is selected before query construction so an
        // internal exception is attributable without retaining a live URL.
        const { type } = data ?? {};
        const isTreatmentV2Enabled = type === "TREATMENT_STARTED";
        const isAttendedFlatWidget = data?.attendedFlatMode === true;
        const errorContext = {
            endpointTemplate: data?.acrossPractice
                ? '/api/v1/leads-flat/'
                : (isTreatmentV2Enabled || isAttendedFlatWidget)
                    ? '/api/v1/practices/:id/lead-treatments-flat/'
                    : '/api/v1/practices/:id/leads/',
            method: 'GET',
        };
        try {
            let query = getQuery(data);
            if (isTreatmentV2Enabled || isAttendedFlatWidget) {
                query = query.replaceAll("lead_treatments__", "");
                if (isTreatmentV2Enabled) {
                    query = `${query}&lead__healthcare_provider=PRIVATE`
                }
            }

            let teethCount = 0;

            // Task N: `practiceOnly` opts a caller OUT of the multi-practice
            // aggregation suffix (callers that must scope to the ACTIVE practice only). Every other
            // getWidgetCounter caller is unaffected: the suffix still applies by
            // default (phase-2 multi-practice aggregation, see _activePracticesSuffix).
            const url = data.acrossPractice
                ? `/api/v1/leads-flat/${query}&${counterOnlyParam}`
                : (isTreatmentV2Enabled || isAttendedFlatWidget)
                    ? `/api/v1/practices/${getPracticeId()}/lead-treatments-flat/${query}&fields={leadtreatment_specification}`
                    : `/api/v1/practices/${getPracticeId()}/leads/${query}&${counterOnlyParam}${data.practiceOnly ? '' : _activePracticesSuffix()}`;
            const response = await callApi(url);
            if (isTreatmentV2Enabled) {
                teethCount = (response?.data?.results ?? [])?.reduce((sum, item) => sum + (item?.leadtreatment_specification?.teeth_count || 0), 0) ?? 0;
            }
            if (response.status === 200) {
                const result = response.data;
                setCounter(data, data.isTreatmentNotPresent ? 0 : result?.count, teethCount);

            } else {
                const payload = buildApiErrorPayload(response, errorContext);
                if (!payload.silent) dispatch(updateError(payload));
            }

        } catch (error) {
            const payload = buildApiErrorPayload(error, errorContext);
            if (!payload.silent) dispatch(updateError(payload));
        }
    };
}
export function getTreatmentWidgetPrice(data) {
    return async () => {
        if (!hasFeatureAccess('analytics-reports')) return;
        const errorContext = {
            endpointTemplate: data?.attendedFlatMode
                ? '/api/v1/practices/:id/reports/lead-treatments/'
                : '/api/v1/practices/:id/reports/leads/',
            method: 'GET',
        };
        try {
            const url = buildTreatmentPriceUrl(getPracticeId(), data, _activePracticesSuffix());
            const response = await callApi(url);

            if (response.status === 200) {
                const result = response.data;
                setCounter(data, data.attendedFlatMode ? result?.data?.sum_custom_price : result?.data?.sum_lead_treatments__custom_price);

            } else {
                const payload = buildApiErrorPayload(response, errorContext);
                if (!payload.silent) dispatch(updateError(payload));
            }
        } catch (error) {
            const payload = buildApiErrorPayload(error, errorContext);
            if (!payload.silent) dispatch(updateError(payload));
        }
    };
}
// Counts ALL leads created in the given date range (no status / provider / archived
// filter), so the Launchpad "New" pipeline card matches the Analytics Overview
// "Total Leads" stat exactly — including archived leads and any current status.
//
// Endpoint: /reports/leads/?aggregate[Count]=id&created_at__gte=<start>&created_at__lte=<end>
// Response shape: { data: { count_id: <N> } }
//
// `dateRange` must be a "YYYY-MM-DD,YYYY-MM-DD" string (same format as creationRange
// in LaunchpadView). The key is stored into the widgetCounter slice exactly like any
// other getWidgetCounter call so LaunchpadPipeline reads it identically.
export function getNewLeadAllCreatedCount({ key, dateRange }) {
    return async () => {
        const errorContext = {
            endpointTemplate: '/api/v1/practices/:id/reports/leads/',
            method: 'GET',
        };
        try {
            const parts = (dateRange || '').split(',');
            const gte = parts[0] ? `${parts[0].trim()} 00:00:00` : '';
            const lte = parts[1] ? `${parts[1].trim()} 23:59:59` : '';
            const dateParams = gte && lte ? `&created_at__gte=${encodeURIComponent(gte)}&created_at__lte=${encodeURIComponent(lte)}` : '';
            const url = `/api/v1/practices/${getPracticeId()}/reports/leads/?aggregate[Count]=id${dateParams}${_activePracticesSuffix()}`;
            const response = await callApi(url);
            if (response.status === 200) {
                dispatch(upsertWidgetCounter({ key, count: response.data?.data?.count_id ?? 0 }));
            } else {
                const payload = buildApiErrorPayload(response, errorContext);
                if (!payload.silent) dispatch(updateError(payload));
            }
        } catch (error) {
            const payload = buildApiErrorPayload(error, errorContext);
            if (!payload.silent) dispatch(updateError(payload));
        }
    };
}

const _readReportsLeadCount = (response) => response?.data?.data?.count_id ?? 0;

// ── Pure Launchpad request builders ─────────────────────────────────────────────
// Practice-explicit URL builders shared by the (current-practice) Redux thunks below AND the
// secondary-practice hook (hooks/useLaunchpadPracticeSummary) so the two can never drift.
// `suffix` is the optional All-Practices "&practice__in=…" suffix (current practice only).
export function buildPipelineCohortUrls(practiceId, dateRange, suffix = '') {
    const parts = (dateRange || '').split(',');
    const gte = parts[0] ? `${parts[0].trim()} 00:00:00` : '';
    const lte = parts[1] ? `${parts[1].trim()} 23:59:59` : '';
    if (!gte || !lte) return null;
    const base = `/api/v1/practices/${practiceId}/reports/leads/?aggregate[Count]=id&created_at__gte=${encodeURIComponent(gte)}&created_at__lte=${encodeURIComponent(lte)}${suffix}&${LEAD_PROVIDER_PARAM}`;
    const withStatuses = (statuses) => `${base}&lead_treatments__status__in=${encodeURIComponent(statuses.join(','))}`;
    return {
        total: base,
        booked: withStatuses(['CONSULTATION_BOOKED', 'CONSULTATION_ATTENDED', 'FAILED_TO_ATTEND_CONSULTATION', 'TREATMENT_STARTED']),
        attended: withStatuses(['CONSULTATION_ATTENDED', 'TREATMENT_STARTED']),
        started: withStatuses(['TREATMENT_STARTED']),
    };
}

export function buildPipelineActivityUrls(practiceId, dateRange, suffix = '') {
    const parts = (dateRange || '').split(',');
    const start = parts[0]?.trim();
    const end = parts[1]?.trim();
    if (!start || !end) return null;
    const base = `/api/v1/practices/${practiceId}/reports/leads/?aggregate[Count]=id${suffix}&${LEAD_PROVIDER_PARAM}`;
    const appointmentRange = `&lead_treatments__appointments__datetime__range=${encodeURIComponent(`${start} 00:00:00,${end} 23:59:00`)}`;
    const startedParams = encodeURIComponent(JSON.stringify({
        state: 'TREATMENT_STARTED',
        timestamp_gte: `${start} 00:00:00`,
        timestamp_lte: `${end} 23:59:00`,
    }));
    return {
        booked: `${base}${appointmentRange}&lead_treatments__appointments__status__in=BOOKED`,
        attended: `${base}${appointmentRange}&lead_treatments__appointments__status__in=ATTENDED`,
        started: `${base}&state_transition_params=${startedParams}`,
    };
}

// The URL getTreatmentWidgetPrice requests (leads aggregate path, or the flat per-treatment path).
export function buildTreatmentPriceUrl(practiceId, data, suffix = '') {
    if (data.attendedFlatMode) {
        let query = getQuery(data).replaceAll('lead_treatments__', '');
        if (data.excludeNhs) query = `${query}&${TREATMENT_LEAD_PROVIDER_PARAM}`;
        query = query.replace('?', '?aggregate[Sum]=custom_price&');
        return `/api/v1/practices/${practiceId}/reports/lead-treatments/${query}${suffix}`;
    }
    return `/api/v1/practices/${practiceId}/reports/leads/${getQuery(data)}${data.excludeNhs ? `&${LEAD_PROVIDER_PARAM}` : ''}${suffix}`;
}

// Started "due now" overdue / due_today probes (lead-treatments-flat, optional custom tab).
export function buildStartedDueNowUrls(practiceId, customTabId = null) {
    const tabParam = customTabId != null ? `&custom_table_tab=${encodeURIComponent(customTabId)}` : '';
    const base = `/api/v1/practices/${practiceId}/lead-treatments-flat/?status=TREATMENT_STARTED&is_archived=false&${TREATMENT_LEAD_PROVIDER_PARAM}&tasks_due_now=true`;
    return {
        overdue: `${base}&tasks_due_bucket=overdue&limit=1&fields={}${tabParam}`,
        dueToday: `${base}&tasks_due_bucket=due_today&limit=1&fields={}${tabParam}`,
    };
}

export const newLeadTaskCountsUrl = (practiceId, searchQuery = '') =>
    `/api/v1/practices/${practiceId}/new-lead-task-widget-counts/${searchQuery && searchQuery.length > 0 ? `?query=${encodeURIComponent(searchQuery)}` : ''}`;

export const attendedTabCountsUrl = (practiceId, customTabId = null) =>
    `/api/v1/practices/${practiceId}/attended-tab-widget-counts/${customTabId != null ? `?custom_tab_id=${encodeURIComponent(customTabId)}` : ''}`;

export const readReportsLeadCount = (response) => response?.data?.data?.count_id ?? 0;

// Launchpad pipeline cohort counts, using the existing reports/leads endpoint.
// The denominator is this month's created leads. These counts answer:
// "Of the leads generated this month, how many have reached this stage now?"
export function getLaunchpadPipelineCohortCounts({ dateRange, keys }) {
    return async () => {
        try {
            if (!keys) return;
            const urls = buildPipelineCohortUrls(getPracticeId(), dateRange, _activePracticesSuffix());
            if (!urls) return;

            const [totalRes, bookedRes, attendedRes, startedRes] = await Promise.all([
                callApi(urls.total),
                callApi(urls.booked),
                callApi(urls.attended),
                callApi(urls.started),
            ]);

            const total = _readReportsLeadCount(totalRes);
            const stageRows = [
                { key: keys.new, count: total, cohortPercent: null },
                { key: keys.booked, count: _readReportsLeadCount(bookedRes) },
                { key: keys.attended, count: _readReportsLeadCount(attendedRes) },
                { key: keys.started, count: _readReportsLeadCount(startedRes) },
            ];

            stageRows.forEach((row) => {
                if (!row.key) return;
                const cohortPercent = total > 0 && row.cohortPercent !== null
                    ? (row.count / total) * 100
                    : row.cohortPercent;
                dispatch(upsertWidgetCounter({ key: row.key, count: row.count, cohortPercent }));
            });
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Something went wrong', cause: error instanceof Error ? new Error(error.message) : new Error(String(error?.message ?? 'error')) }));
        }
    };
}

export function getLaunchpadPipelineActivityTotals({ dateRange, keys }) {
    return async () => {
        try {
            if (!keys) return;
            const urls = buildPipelineActivityUrls(getPracticeId(), dateRange, _activePracticesSuffix());
            if (!urls) return;

            const [bookedRes, attendedRes, startedRes] = await Promise.all([
                callApi(urls.booked),
                callApi(urls.attended),
                callApi(urls.started),
            ]);

            [
                { key: keys.booked, count: _readReportsLeadCount(bookedRes) },
                { key: keys.attended, count: _readReportsLeadCount(attendedRes) },
                { key: keys.started, count: _readReportsLeadCount(startedRes) },
            ].forEach((row) => {
                if (row.key) dispatch(upsertWidgetCounter({ key: row.key, count: row.count }));
            });
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Something went wrong', cause: error instanceof Error ? new Error(error.message) : new Error(String(error?.message ?? 'error')) }));
        }
    };
}

export function getTaskWidgetCounter(data) {
    return async () => {
        try {
            // Dashboard follow-up widgets: on tasks-v2-enabled practices the badge
            // must count V2 tasks (Overdue + Due Today, whole practice) SCOPED to the
            // widget's lead stage, so it matches the V2 task board the click lands on
            // (filtered to that stage) — NOT the legacy V1 /reminders/ (key '2') or
            // lead-based (key '4') set:
            //   key '2' New patient follow ups  → V2 tasks on NEW-stage lead_treatments
            //   key '4' Consultation follow ups → V2 tasks on CONSULTATION_ATTENDED-stage
            // The lead-detail task-filter path (data.taskFilter) and all V1 practices
            // keep their existing behaviour untouched (key '2' /reminders/, key '4'
            // lead-based getWidgetCounter — routed there in useReloadWidgetCounter).
            const practiceId = getPracticeId();
            if (!data?.taskFilter && data?.v2FollowUpStage && hasFeatureAccess('tasks-v2', practiceId)) {
                const { overdue, dueToday, total } = await getV2FollowUpTaskCount(practiceId, data.v2FollowUpStage);
                // V2 path: store BOTH columns separately (overdue / dueToday) plus
                // their sum as `count` so the dashboard card can render two labelled
                // numbers. `isV2Split` flags the card to use the two-number layout.
                dispatch(upsertWidgetCounter({ key: data.key, count: total, overdue, dueToday, isV2Split: true }));
                return;
            }
            // WhatsApp widget (key '7') on tasks-v2 practices: counts pending/in_progress
            // WHATSAPP_SENT tasks. This is the task-side component of the badge; the
            // thread-side component (needs-reply virtual cards) is added in GeneralApp
            // via buildWhatsAppVirtualCards(threadMessages). Together they represent
            // "conversations needing attention". The widget click routes to the WhatsApp
            // inbox (not the tasks board) on both V1 and V2 practices — see GeneralApp
            // handleCardClick case '7'. Non-tasks-v2 practices use fetchUnreadWhatsAppCount
            // (not routed here). isV2WhatsApp without tasks-v2 access → no-op (don't
            // fall through to the V1 reminders endpoint, which doesn't know about tasks).
            if (!data?.taskFilter && data?.isV2WhatsApp) {
                if (hasFeatureAccess('tasks-v2', practiceId)) {
                    const count = await getV2WhatsAppTaskCount(practiceId);
                    dispatch(upsertWidgetCounter({ key: data.key, count }));
                }
                return;
            }

            const response = await callApi(`/api/v1/practices/${practiceId}/reminders/${data?.taskFilter ? getQueryTask(data) : getQuery(data)}&${counterOnlyParam}`);

            if (response.status === 200) {
                const result = response.data;
                // V1 (legacy reminders) path keeps the single-number display.
                // Clear any V2 split fields so a V2→V1 practice switch can't leave
                // a stale "Overdue / Due Today" layout on the merged widget entry.
                dispatch(upsertWidgetCounter({ key: data.key, count: result?.count, overdue: undefined, dueToday: undefined, isV2Split: false }));
            } else if (isOldRemindersDisabled(response)) {
                // Old reminder system disabled for this practice: show 0, no error toast.
                dispatch(upsertWidgetCounter({ key: data.key, count: 0, overdue: undefined, dueToday: undefined, isV2Split: false }));
            } else {
                dispatch(updateError({ success: false, message: response.data || 'Something went wrong', status: response?.status }));
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Something went wrong', cause: error instanceof Error ? new Error(error.message) : new Error(String(error?.message ?? 'error')) }));
        }
    };
}

// Tab-contextual attended widget counts — single request per tab switch.
// Returns all 6 widget numbers from the BE in one response so there is no
// per-widget request storm (CPU incident 2026-05-26).
// `customTabId` — the active custom tab id (number), or null for whole-practice counts.
//
// Follows the same thunk pattern as getWidgetCounter/getTaskWidgetCounter in this file:
// returns an async () => {...} that uses the module-level imported `dispatch`.
//
// Stale-response guard: uses a generation counter (generationRef) instead of
// AbortController. callApi does not accept a signal param, so AbortController
// only created a false sense of cancellation — the request was never actually
// cancelled, and callApi's 8s GET dedupe + 5xx retries widened the race window.
// The generation counter approach: increment on every trigger, capture the value
// in the closure, and on response check captured === current before dispatching.
// The caller passes in generationRef (a React ref) and increments it before each call.
export function getAttendedTabWidgetCounts(practiceId, customTabId, generationRef, { bustCache = false, priority = undefined } = {}) {
    const capturedGeneration = generationRef.current;
    const thunk = async () => {
        dispatch(setAttendedTabCountsLoading(true));
        try {
            // Fetch widget counts and the tab-scoped All treatment-row count in parallel.
            // The widget endpoint accepts custom_tab_id; the flat endpoint accepts
            // custom_table_tab — different param names, same resolved tab on the BE.
            // Using the correct param here is critical: DRF silently ignores unknown
            // params, so custom_tab_id on the leads endpoint returns the practice-wide
            // count rather than the tab-scoped total.
            const allCountUrl = customTabId != null
                ? `/api/v1/practices/${practiceId}/lead-treatments-flat/?is_archived=false&status=CONSULTATION_ATTENDED&custom_table_tab=${encodeURIComponent(customTabId)}&${TREATMENT_LEAD_PROVIDER_PARAM}&limit=1&fields={}`
                : null;
            const [res, allRes] = await Promise.all([
                callApi(
                    attendedTabCountsUrl(practiceId, customTabId),
                    'GET',
                    null,
                    false,
                    { bustCache, ...(priority ? { priority } : {}) },
                ),
                allCountUrl
                    ? callApi(allCountUrl, 'GET', null, false, { bustCache })
                    : Promise.resolve(null),
            ]);
            // Drop stale responses from prior tab selections. Note: callApi dedupes
            // identical GETs within 8s, so a rapid switch back to the same tab returns
            // the cached promise (not a new request) — the generation check still fires
            // and correctly dispatches because capturedGeneration matches current.
            if (capturedGeneration !== generationRef.current) return;
            if (res?.status === 200 && res.data) {
                const all = (allRes?.status === 200 && typeof allRes?.data?.count === 'number')
                    ? allRes.data.count
                    : null;
                dispatch(setAttendedTabCounts({ ...res.data, all }));
            } else {
                dispatch(setAttendedTabCountsLoading(false));
            }
        } catch (err) {
            if (capturedGeneration === generationRef.current) {
                dispatch(updateError({
                    success: false,
                    message: err.message || 'Something went wrong',
                    cause: err instanceof Error ? new Error(err.message) : new Error(String(err?.message ?? 'error')),
                }));
                dispatch(setAttendedTabCountsLoading(false));
            }
        }
    };
    return thunk;
}

// Launchpad "Needs you today": "due now" TOTAL per custom tab, for the Attended and Started
// cards (one chip per tab). Tabs come from the same custom-table-tabs list the tables use
// (top-strip, visible); counts per tab:
//   attended → attended-tab-widget-counts/?custom_tab_id=<id>  → due_now.due_now_total
//   started  → the two tasks_due_now bucket queries (overdue + due_today) scoped with
//              custom_table_tab=<id>, summed (same as getTsContextualWidgetCounts).
// A failed probe stores null for that tab (rendered "—"), never a fake 0. A table with no
// tabs stores {} so the card keeps its Overdue / Due today chips.
const LAUNCHPAD_TAB_TABLES = [
    { kind: 'attended', parent: 'Consultation Attended Leads' },
    { kind: 'started', parent: 'Treatment Started Leads' },
];

export async function fetchAttendedTabDueNow(practiceId, tabId) {
    const res = await callApi(`/api/v1/practices/${practiceId}/attended-tab-widget-counts/?custom_tab_id=${encodeURIComponent(tabId)}`);
    const due = res?.status === 200 ? res.data?.due_now : null;
    if (!due) return null;
    if (due.due_now_total != null) return due.due_now_total;
    if (due.overdue == null && due.due_today == null) return null;
    return (due.overdue || 0) + (due.due_today || 0);
}

export async function fetchStartedTabDueNow(practiceId, tabId) {
    const base = `/api/v1/practices/${practiceId}/lead-treatments-flat/?status=TREATMENT_STARTED&is_archived=false&${TREATMENT_LEAD_PROVIDER_PARAM}&tasks_due_now=true&limit=1&fields={}&custom_table_tab=${encodeURIComponent(tabId)}`;
    const [overdueRes, dueTodayRes] = await Promise.all([
        callApi(`${base}&tasks_due_bucket=overdue`),
        callApi(`${base}&tasks_due_bucket=due_today`),
    ]);
    const read = (r) => (r?.status === 200 && typeof r?.data?.count === 'number' ? r.data.count : null);
    const o = read(overdueRes);
    const d = read(dueTodayRes);
    if (o === null && d === null) return null;
    return (o || 0) + (d || 0);
}

export function getLaunchpadDueNowByTab(practiceId) {
    return async () => {
        if (!practiceId) return;
        try {
            await dispatch(loadCustomTabs(practiceId));
        } catch (err) {
            return;
        }
        const slot = store.getState().customTableTabs?.byPractice?.[practiceId];
        await Promise.all(LAUNCHPAD_TAB_TABLES.map(async ({ kind, parent }) => {
            const tabs = (slot?.byParent?.[parent] || []).filter((t) => t.placement === 'top' && !t.is_hidden);
            const counts = {};
            await Promise.all(tabs.map(async (tab) => {
                try {
                    counts[tab.id] = kind === 'attended'
                        ? await fetchAttendedTabDueNow(practiceId, tab.id)
                        : await fetchStartedTabDueNow(practiceId, tab.id);
                } catch (err) {
                    counts[tab.id] = null;
                }
            }));
            // Drop the result if the user switched practice while we were fetching.
            if (String(store.getState().practiceTreatmentList?.practiceId) !== String(practiceId)) return;
            dispatch(setDueNowByTab({ kind, counts }));
        }));
    };
}

// Treatment Started checklist strip counts — three parallel limit=1 calls to
// lead-treatments-flat: in_progress, complete, and the unfiltered All total.
// Mirrors getAttendedTabWidgetCounts: same generation-counter stale-response guard,
// same module-level `dispatch`, same thunk-returning pattern.
//
// Called by TreatmentStarted.jsx on every custom-tab switch (via a useEffect keyed
// on activeCustomTabId). The caller increments generationRef.current BEFORE calling
// this so a rapid switch discards any in-flight response from the previous tab.
//
// Only fires for checklist-bound custom tabs (caller guards with checklist_binding
// check). Including the All count here (rather than reading pageCount from the table
// response) avoids the cold-load race where an early unscoped table fetch overwrites
// the scoped count before the tab context has settled.
export function getTsChecklistCounts(practiceId, customTabId, generationRef, { bustCache = false } = {}) {
    const capturedGeneration = generationRef.current;
    const thunk = async () => {
        try {
            const base = `/api/v1/practices/${practiceId}/lead-treatments-flat/?status=TREATMENT_STARTED&custom_table_tab=${encodeURIComponent(customTabId)}&is_archived=false&${TREATMENT_LEAD_PROVIDER_PARAM}&limit=1&fields={}`;
            const [inProgressRes, completeRes, allRes] = await Promise.all([
                callApi(`${base}&checklist_state=in_progress`, 'GET', null, false, { bustCache }),
                callApi(`${base}&checklist_state=complete`, 'GET', null, false, { bustCache }),
                callApi(base, 'GET', null, false, { bustCache }),
            ]);
            // Drop stale responses from prior tab selections.
            if (capturedGeneration !== generationRef.current) return;
            const readCount = (res) => (res?.status === 200 && typeof res?.data?.count === 'number' ? res.data.count : null);
            dispatch(setTsChecklistCounts({
                in_progress: readCount(inProgressRes),
                complete: readCount(completeRes),
                all: readCount(allRes),
            }));
        } catch (err) {
            if (capturedGeneration === generationRef.current) {
                dispatch(updateError({
                    success: false,
                    message: err.message || 'Something went wrong',
                    cause: err instanceof Error ? new Error(err.message) : new Error(String(err?.message ?? 'error')),
                }));
            }
        }
    };
    return thunk;
}

// Fetch counts for the New Lead table "Tasks / Due" mode widget.
// Returns { overdue, due_today, upcoming, no_next_action } from the
// new-lead-task-widget-counts endpoint — NEW-status, non-archived leads,
// one bucket per lead based on their soonest active task (or none).
//
// Graceful degradation: if the endpoint returns non-200 (e.g. not yet
// deployed on the current backend) all counts silently default to 0.
// This prevents a crash / error toast when the prod backend is behind.
// searchQuery: when provided (search active in tasks-mode), the BE scopes each
// bucket count to only NEW leads matching the query so the cards reflect the
// match breakdown rather than the practice-wide totals. Requires BE #43.
export function getNewLeadTaskWidgetCounts(practiceId, searchQuery, generationRef = null, { priority = undefined } = {}) {
    const capturedGeneration = generationRef?.current;
    const isStale = () => generationRef && capturedGeneration !== generationRef.current;
    const thunk = async () => {
        dispatch(setNewLeadTaskCountsLoading(true));
        try {
            const countsUrl = newLeadTaskCountsUrl(practiceId, searchQuery);
            const res = priority ? await callApi(countsUrl, 'GET', null, false, { priority }) : await callApi(countsUrl);
            if (isStale()) return;
            if (res?.status === 200 && res.data) {
                dispatch(setNewLeadTaskCounts(res.data));
            } else {
                // Endpoint not yet deployed or returned an error (429, 5xx…) — counts are UNKNOWN, not zero.
                dispatch(clearNewLeadTaskCounts());
            }
        } catch (err) {
            if (isStale()) return;
            // Network error or unexpected exception — unknown (null → "—"), no crash, no toast.
            dispatch(clearNewLeadTaskCounts());
        }
    };
    return thunk;
}

// Fetch a TREATMENT_STARTED population from /lead-treatments-flat/ and group it client-side
// by treatment name. Keep the fields deliberately close to the legacy widget path: only the
// treatment item name and teeth count are needed for the chips, not the full treatment object.
//
// Dynamic: discovers whatever treatment names exist for THIS practice/tab (no hardcoded
// Invisalign/teeth-straightening/etc). Returns [{ name, count, teeth }] sorted by count desc.
//
// The BE caps page size at 500, so we paginate. A hard safety ceiling (maxRows) prevents an
// unbounded fetch on a pathologically large tab — if hit, we set `truncated: true` and log a
// warning rather than silently dropping rows (per the cost note in the spec).
async function fetchTreatmentStartedBreakdown(practiceId, extraParams = '', { bustCache = false, label = 'treatment breakdown' } = {}) {
    const PAGE_SIZE = 500;
    const MAX_ROWS = 5000; // 10 pages — generous ceiling; flag + warn if exceeded
    const fieldsSel = '&fields={id,treatment{treatment_item{name}},leadtreatment_specification{teeth_count}}';
    const base = `/api/v1/practices/${practiceId}/lead-treatments-flat/?status=TREATMENT_STARTED&is_archived=false&${TREATMENT_LEAD_PROVIDER_PARAM}${extraParams}${fieldsSel}`;

    const map = new Map(); // name → { count, teeth }
    let offset = 0;
    let total = null;
    let fetched = 0;
    let truncated = false;
    let aborted = false; // a page returned non-200 (abort / stale / error) before completing

    // Sequential pagination — each page depends on the prior page's row count.
    // NO abort+retry: the caller's generation-ref discards stale results, and the fetch is
    // now dispatched at most once per (practice, tab), so there is no concurrent re-invocation
    // to abort against. Retrying with bustCache here was what evicted in-flight entries and
    // produced the ERR_ABORTED flood.
    // eslint-disable-next-line no-constant-condition
    while (true) {
        const pageUrl = `${base}&limit=${PAGE_SIZE}&offset=${offset}`;
        // eslint-disable-next-line no-await-in-loop
        const res = await callApi(pageUrl, 'GET', null, false, { bustCache });
        if (res?.status !== 200 || !res?.data) {
            // Aborted / stale / error mid-pagination — the breakdown is INCOMPLETE. Flag it
            // so the caller does NOT merge a partial/empty result over good chips. (A genuine
            // empty tab still returns status 200 with an empty results array, so aborted stays
            // false in that case.)
            aborted = true;
            break;
        }
        if (total === null) total = typeof res.data.count === 'number' ? res.data.count : 0;
        const rows = res.data.results || [];
        rows.forEach((r) => {
            const name = r?.treatment?.treatment_item?.name || 'Unknown';
            const teeth = r?.leadtreatment_specification?.teeth_count || 0;
            const entry = map.get(name) || { count: 0, teeth: 0 };
            entry.count += 1;
            entry.teeth += teeth;
            map.set(name, entry);
        });
        fetched += rows.length;
        if (rows.length < PAGE_SIZE || fetched >= (total || 0)) break;
        if (fetched >= MAX_ROWS) {
            truncated = true;
            // eslint-disable-next-line no-console
            console.warn(`[getTsContextualWidgetCounts] ${label} truncated at ${MAX_ROWS} rows (total ${total}) — per-treatment counts will not sum to the headline total.`);
            break;
        }
        offset += PAGE_SIZE;
    }

    const breakdown = Array.from(map.entries())
        .map(([name, v]) => ({ name, count: v.count, teeth: v.teeth }))
        .sort((a, b) => b.count - a.count);

    return { breakdown, truncated, aborted };
}

// Treatment Started contextual widget counts — parallel tab-scoped requests for:
// Due Now (overdue + due_today), In Progress, Follow-Up Complete, the active tab's
// go-aheads total + per-treatment breakdown, Started This Month (count + £ value).
//
// Due Now uses lead-treatments-flat with tasks_due_now=true + task_due_bucket=<overdue|
// due_today> + custom_table_tab (BE-resolved, tab-scoped).
//
// Started This Month uses the leads API with state_transition_params targeting the
// TREATMENT_STARTED transition within the current calendar month (1st → today), scoped to
// the active custom_table_tab. The matching £ value uses /reports/lead-treatments/ with
// aggregate[Sum]=custom_price (the /reports/leads/ endpoint ignores custom_table_tab).
//
// customTabId: optional — when provided, scopes every count to that custom tab.
// invisalignTreatmentIds: array of treatment IDs (Invisalign / Teeth Straightening / Fixed
//   Braces) resolved by the page. Used to fetch the "Invisalign go aheads" count DIRECTLY
//   the active top tab's "All" total (the "<tab name> go aheads" card). It is scoped to
//   the SAME custom_table_tab the active tab uses for its own All total — so it shows the
//   active tab's distinct number (Invisalign → its total, Cosmetic & Implants → its total,
//   etc.), re-fetching on tab switch. The tab param ALONE is used (no treatment__in): the
//   BE resolves the tab's own treatment binding, and combining tab + explicit treatment__in
//   returns 0. On the built-in board (no active custom tab) the count is null and the card
//   falls back to a practice-wide treatment-scoped Invisalign count via invisalignTreatmentIds.
// generationRef: stale-response guard (same pattern as getAttendedTabWidgetCounts).
// bustCache: bypass 8s GET dedupe for post-mutation refetches.
export function getTsContextualWidgetCounts(practiceId, customTabId, generationRef, { invisalignTreatmentIds = [], bustCache = false, dateRange = null, dateScopedOnly = false, priority = undefined } = {}) {
    const capturedGeneration = generationRef.current;
    const thunk = async () => {
        dispatch(setTsContextualCountsLoading(true));
        try {
            const now = dayjs();
            const startOfMonth = now.startOf('month').format('YYYY-MM-DD');
            const today = now.format('YYYY-MM-DD');

            const tabParam = customTabId != null ? `&custom_table_tab=${encodeURIComponent(customTabId)}` : '';

            // Due Now: tab-scoped overdue / due_today counts, split into TWO queries so the
            // card can show the Overdue/Due Today chips (Task N).
            //
            // Task N root cause fix: the PREVIOUS two-query version sent `task_due_bucket`
            // (singular) — that param is declared on LeadFilter (apps/leads/filters.py:333),
            // NOT on LeadTreatmentFilter, and this is the lead-treatments-flat endpoint, which
            // uses the latter. django-filter silently ignores unknown params, so both queries
            // returned the SAME unbucketed due-now count and the headline rendered double (a
            // card reading 8 (4 + 4) over a table of 4) — which is why this was collapsed to one
            // unbucketed query and isV2Split was turned off.
            //
            // The correct, DECLARED LeadTreatmentFilter param is `tasks_due_bucket` (plural) —
            // confirmed live on staging+prod already for `/leads/` and `/lead-treatments-flat/`
            // alongside `tasks_due_now=true`. Using the right param name restores the split:
            // due_now_total is computed client-side as overdue + due_today (both disjoint on
            // the BE, same convention as NewLeadTaskDueWidget's due_now fallback) rather than a
            // third unbucketed query, so this stays at two requests, not three.
            const { overdue: overdueQuery, dueToday: dueTodayQuery } = buildStartedDueNowUrls(practiceId, customTabId);

            // In Progress / Follow-Up Complete: tab-scoped checklist_state counts. Fetched here
            // (not via getTsChecklistCounts) so they work on EVERY tab — including exclude-mode
            // tabs with no checklist_binding.template_ids (e.g. "All Other"), where the page-level
            // getTsChecklistCounts dispatch was skipped, leaving the cards stuck at 0.
            const checklistBase = `/api/v1/practices/${practiceId}/lead-treatments-flat/?status=TREATMENT_STARTED&is_archived=false&${TREATMENT_LEAD_PROVIDER_PARAM}&limit=1&fields={}${tabParam}`;
            const inProgressQuery = `${checklistBase}&checklist_state=in_progress`;
            const followUpCompleteQuery = `${checklistBase}&checklist_state=complete`;

            // Started card: TREATMENT rows that ENTERED treatment (transitioned to
            // TREATMENT_STARTED) within the ACTIVE table date range, SCOPED to the active tab.
            // MUST use lead-treatments-flat (one row per treatment) — NOT /leads/ (distinct
            // leads) — so the card count equals what clicking it filters the table to. The
            // TS table + its calendar filter both query lead-treatments-flat with this exact
            // state_transition_params (state TREATMENT_STARTED, <start> 00:00:00 → <end>
            // 23:59:00); a lead with 2 TS treatments counts as 2 rows here, matching the table.
            //
            // Range basis: the caller passes the table's active date-picker range
            // (dateRange.startDate / endDate, 'YYYY-MM-DD'). When no range is active we fall
            // back to the current calendar month (1st → today) — the historical "Started This
            // Month" default. rangeStart/End reused for the matching £ value below.
            const hasActiveRange = Boolean(dateRange && dateRange.startDate && dateRange.endDate);
            const rangeStart = `${hasActiveRange ? dateRange.startDate : startOfMonth} 00:00:00`;
            const rangeEnd = `${hasActiveRange ? dateRange.endDate : today} 23:59:00`;
            const startedParams = encodeURIComponent(`{"state":"TREATMENT_STARTED","timestamp_gte":"${rangeStart}","timestamp_lte":"${rangeEnd}"}`);
            const startedQuery = `/api/v1/practices/${practiceId}/lead-treatments-flat/?status=TREATMENT_STARTED&is_archived=false&${TREATMENT_LEAD_PROVIDER_PARAM}&state_transition_params=${startedParams}${tabParam}&limit=1&fields={}`;

            // Total treatment value — the £ companion of the "Started" count, so it MUST sum
            // exactly the population the count/table show. Uses /reports/lead-treatments/ with
            // aggregate[Sum]=custom_price + custom_table_tab. NOTE: the /reports/leads/ endpoint
            // IGNORES custom_table_tab (returns the practice-wide sum on every tab), so we use
            // the per-treatment reports endpoint here, which honours the tab.
            //
            // status=TREATMENT_STARTED is REQUIRED here to match startedQuery above: without it
            // the aggregate summed every treatment that EVER transitioned to TREATMENT_STARTED in
            // the range, including ones that have since moved on (completed/closed), producing the
            // "0 started but £X value" contradiction. The count filters on the CURRENT status, so
            // the value must too.
            //
            // NHS is excluded (lead__healthcare_provider__in — the form the flat/report endpoints honour,
            // see utils/healthcareProviderScope) on the count AND this sum in lockstep, so value == count.
            // Gated on analytics-reports access just like getTreatmentWidgetPrice; without
            // access the value stays null and the card shows a dash.
            const canReadReports = hasFeatureAccess('analytics-reports', practiceId);
            const startedValueQuery = canReadReports
                ? `/api/v1/practices/${practiceId}/reports/lead-treatments/?status=TREATMENT_STARTED&is_archived=false&${TREATMENT_LEAD_PROVIDER_PARAM}&aggregate[Sum]=custom_price&state_transition_params=${startedParams}${tabParam}`
                : null;

            // "<tab name> go aheads" — active tab's "All" total, scoped to its own
            // custom_table_tab (no treatment__in). When an active custom tab is present we
            // use the tab-scoped count (the tab's distinct number). On the built-in board
            // (customTabId == null) we fall back to a practice-wide treatment-scoped
            // Invisalign count so the card still shows a sensible "Invisalign go aheads" value.
            const invisIds = (invisalignTreatmentIds || []).filter(Boolean).join(',');
            let tabGoAheadsQuery = null;
            if (customTabId != null) {
                tabGoAheadsQuery = `/api/v1/practices/${practiceId}/lead-treatments-flat/?status=TREATMENT_STARTED&is_archived=false&${TREATMENT_LEAD_PROVIDER_PARAM}&limit=1&fields={}${tabParam}`;
            } else if (invisIds) {
                tabGoAheadsQuery = `/api/v1/practices/${practiceId}/lead-treatments-flat/?status=TREATMENT_STARTED&treatment__in=${invisIds}&is_archived=false&${TREATMENT_LEAD_PROVIDER_PARAM}&limit=1&fields={}`;
            }

            // PHASE 1 — fast count/value queries (all limit=1 or single-aggregate). These
            // paint the headline cards quickly. The (potentially paginated) per-treatment
            // breakdown is fetched AFTERWARDS in phase 2 so the numbers aren't held hostage
            // to the breakdown's pagination on big tabs.
            const [overdueRes, dueTodayRes, startedRes, goAheadsRes, startedValueRes, inProgressRes, followUpCompleteRes, totalRes] = await Promise.all([
                dateScopedOnly ? Promise.resolve(null) : callApi(overdueQuery, 'GET', null, false, { bustCache, ...(priority ? { priority } : {}) }),
                dateScopedOnly ? Promise.resolve(null) : callApi(dueTodayQuery, 'GET', null, false, { bustCache, ...(priority ? { priority } : {}) }),
                callApi(startedQuery, 'GET', null, false, { bustCache }),
                !dateScopedOnly && tabGoAheadsQuery ? callApi(tabGoAheadsQuery, 'GET', null, false, { bustCache }) : Promise.resolve(null),
                startedValueQuery ? callApi(startedValueQuery, 'GET', null, false, { bustCache }) : Promise.resolve(null),
                dateScopedOnly ? Promise.resolve(null) : callApi(inProgressQuery, 'GET', null, false, { bustCache }),
                dateScopedOnly ? Promise.resolve(null) : callApi(followUpCompleteQuery, 'GET', null, false, { bustCache }),
                // Unfiltered Started total for the active tab — the grey "N started" chip.
                dateScopedOnly ? Promise.resolve(null) : callApi(checklistBase, 'GET', null, false, { bustCache }),
            ]);

            if (capturedGeneration !== generationRef.current) return;

            // A FAILED count probe resolves to null — never a hard 0, and never a carried-over
            // previous value. These are heavy COUNT queries against a 10s callApi timeout, so a
            // cold cache can return status 0/5xx; the old `: 0` fallback rendered that as a real
            // "0" beside a table full of rows (the reported "filter shows 0, then fixes itself").
            // Showing the last painted number instead would be just as bad — a stale figure the
            // user cannot tell is stale. null means "unknown": the widget renders "—" for that
            // card and offers a retry.
            const readCount = (res) => {
                if (res?.status === 200 && typeof res?.data?.count === 'number') return res.data.count;
                return null;
            };
            const readPriceSum = (res) => {
                // /reports/lead-treatments/ returns data.sum_custom_price (the aggregate key
                // mirrors the requested field: aggregate[Sum]=custom_price → sum_custom_price).
                const sum = res?.data?.data?.sum_custom_price;
                return res?.status === 200 && typeof sum === 'number' ? sum : null;
            };

            const phaseOneCounts = {
                started_this_month: readCount(startedRes),
                started_this_month_value: startedValueRes ? readPriceSum(startedValueRes) : null,
            };
            if (dateScopedOnly) {
                dispatch(mergeTsContextualCounts(phaseOneCounts));
            } else {
                const overdueCount = readCount(overdueRes);
                const dueTodayCount = readCount(dueTodayRes);
                // Null (unknown — a failed probe) must propagate as null, not be silently
                // treated as 0 and summed into a false "0 due now" headline.
                const dueNowTotal = (overdueCount === null && dueTodayCount === null)
                    ? null
                    : (overdueCount || 0) + (dueTodayCount || 0);
                dispatch(setTsContextualCounts({
                    due_now_total: dueNowTotal,
                    overdue: overdueCount,
                    due_today: dueTodayCount,
                    ...phaseOneCounts,
                    // tab_go_aheads: the active tab's All total (or practice-wide Invisalign on built-in board)
                    tab_go_aheads: goAheadsRes ? readCount(goAheadsRes) : null,
                    started_this_month_breakdown: null,
                    started_this_month_truncated: false,
                    // Tab-scoped checklist counts — work on every tab incl exclude-mode (no binding).
                    in_progress: readCount(inProgressRes),
                    follow_up_complete: readCount(followUpCompleteRes),
                    started_total: readCount(totalRes),
                }));
            }

            // PHASE 2 — deferred per-treatment breakdown (dynamic, client-side grouped).
            // Only on an active custom tab (built-in board has no single-tab population to
            // group). Merged into the already-painted counts; the generation guard discards
            // it if the user switched tabs while it was paginating.
            //
            // Abort safety: if a page was aborted mid-pagination (cold-load nav, stale GET),
            // the breakdown is INCOMPLETE — do NOT merge it (a partial/empty list over a valid
            // headline shows e.g. "589" with no chips, or chips that don't sum). Leave the
            // prior breakdown (null while loading) and let the next fetch populate it. Wrapped
            // in its own try so a breakdown failure never wipes the phase-1 counts.
            try {
                const breakdownRequests = [
                    fetchTreatmentStartedBreakdown(
                        practiceId,
                        `&state_transition_params=${startedParams}${tabParam}`,
                        { bustCache, label: 'started-range breakdown' }
                    ),
                ];
                const [startedBreakdownResult] = await Promise.all(breakdownRequests);
                if (capturedGeneration !== generationRef.current) return;

                const mergePayload = {};
                if (startedBreakdownResult && !startedBreakdownResult.aborted) {
                    mergePayload.started_this_month_breakdown = startedBreakdownResult.breakdown;
                    mergePayload.started_this_month_truncated = startedBreakdownResult.truncated;
                }
                if (Object.keys(mergePayload).length > 0) {
                    dispatch(mergeTsContextualCounts(mergePayload));
                }
                // aborted → no-op merge: keep prior chips / null, next fetch repopulates.
            } catch {
                // Breakdown threw — leave phase-1 counts intact, no merge.
            }
        } catch (err) {
            if (capturedGeneration === generationRef.current) {
                // Phase-1 (count) failure. Graceful degradation — no crash, no toast.
                // (Phase-2 breakdown failures are handled above and never reach here, so a
                // breakdown abort can't wipe good counts.)
                //
                // Every affected count becomes null, NOT 0 and NOT the previously painted
                // value: the widget then shows "—" plus a retry control, so a load failure can
                // never be misread as "we have no go-aheads this month". A date-scoped failure
                // only blanks the two date-scoped cards; the rest keep their fetched values.
                if (dateScopedOnly) {
                    dispatch(mergeTsContextualCounts({ started_this_month: null, started_this_month_value: null }));
                } else {
                    dispatch(setTsContextualCounts({ due_now_total: null, started_this_month: null, started_this_month_value: null, started_this_month_breakdown: null, started_this_month_truncated: false, tab_go_aheads: null, in_progress: null, follow_up_complete: null, started_total: null }));
                }
            }
        }
    };
    return thunk;
}
// Archive-audit summary (Phase 2). Mirrors archivedLead()'s filter params so the
// widget numbers ALWAYS describe the same filtered dataset as the table — the BE
// action shares the table's LeadFilter, so parity holds end-to-end.
// Graceful degradation: non-200 (BE not yet deployed) → null, no toast, no crash;
// consumers hide the widgets when summary is null.
export function getArchivedSummary({ practiceId, treatment, searchQuery, tags, referral, startDate, endDate, archiveReason, noTreatment }) {
    const thunk = async () => {
        dispatch(setArchivedSummaryLoading(true));
        try {
            const treatmentQuery = noTreatment ? '&lead_treatments__isnull=true' : treatment ? `&lead_treatments__treatment__in=${treatment}` : '';
            const search = searchQuery ? `&search_query=${encodeURIComponent(searchQuery)}` : '';
            const tagsQuery = tags ? `&tagged_items__tag__name__in=${tags}` : '';
            const referralQuery = referral ? `&lead_source__referral__in=${referral}` : '';
            const dateQuery = (startDate && endDate) ? `&archived_at__range=${`${startDate} 00:00:00`},${`${endDate} 23:59:00`}` : '';
            const reasonQuery = archiveReason ? `&archived_reason__in=${archiveReason}` : '';
            const res = await callApi(`/api/v1/practices/${practiceId}/archived/summary/?is_archived=true${treatmentQuery}${search}${tagsQuery}${referralQuery}${dateQuery}${reasonQuery}`);
            if (res?.status === 200 && res.data) {
                dispatch(setArchivedSummary(res.data));
            } else {
                dispatch(setArchivedSummary(null));
            }
        } catch (err) {
            dispatch(setArchivedSummary(null));
        }
    };
    return thunk;
}
